import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import DOMPurify from "dompurify";
import { GetAxios } from "../../helpers/axiosHelpers";
import { LogoIcon } from "../Icons";
import { useLanguage } from "../../contexts/LanguageContext";

// Force all external links to open safely — prevents tab-napping
DOMPurify.addHook("afterSanitizeAttributes", (node) => {
  if (node.tagName === "A") {
    node.setAttribute("rel", "noopener noreferrer");
    // Only set target="_blank" for external links (starting with http)
    const href = node.getAttribute("href") || "";
    if (href.startsWith("http")) {
      node.setAttribute("target", "_blank");
    }
  }
});

/**
 * Generic public content page viewer.
 * Automatically shows EN or JA content based on the app's global language setting.
 * Falls back to English if Japanese content has not been entered yet.
 *
 * Props:
 *   slug      – content page slug, e.g. "privacy-policy"
 *   category  – small label shown above the title, e.g. "Legal"
 */
const ContentPage = ({ slug, category = "Legal" }) => {
  const { language, toggleLanguage } = useLanguage();

  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    GetAxios(`/content-pages/public/${slug}`)
      .then((res) => {
        if (res?.success && res.data) {
          setPage(res.data);
        } else {
          setNotFound(true);
        }
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  const isJa = language === "ja";
  const hasJapanese = Boolean(page?.titleJa || page?.contentJa);

  // Show Japanese content only when: language is JA AND Japanese content exists.
  // Otherwise always fall back to English — never show a blank page.
  const displayTitle = isJa && page?.titleJa ? page.titleJa : page?.title;
  const displayContent =
    isJa && page?.contentJa ? page.contentJa : page?.content;
  const showFallbackNotice = isJa && !hasJapanese;

  return (
    <div className="min-h-screen bg-white font-sans">
      {/* ── Nav ─────────────────────────────────────────────────────── */}
      <header className="border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/">
            <LogoIcon className="h-7 w-auto text-subidha-blue" />
          </Link>
          <div className="flex items-center gap-4">
            {/* Language toggle — mirrors the site-wide toggle */}
            <button
              onClick={toggleLanguage}
              className="text-[10px] font-black uppercase tracking-widest px-3 py-1.5 rounded-xl bg-slate-100 text-slate-500 hover:bg-subidha-blue hover:text-white transition-all"
              title="Switch language"
            >
              {isJa ? "🇬🇧 EN" : "🇯🇵 JA"}
            </button>
            <Link
              to="/"
              className="text-[10px] font-black uppercase tracking-widest text-slate-400 hover:text-subidha-blue transition-colors"
            >
              ← {isJa ? "ホームへ戻る" : "Back to Home"}
            </Link>
          </div>
        </div>
      </header>

      {/* ── Content ─────────────────────────────────────────────────── */}
      <main className="max-w-4xl mx-auto px-6 py-16">
        {loading && (
          <div className="flex flex-col items-center justify-center py-32 opacity-30">
            <div className="w-10 h-10 border-4 border-subidha-blue border-t-transparent rounded-full animate-spin mb-4" />
            <p className="font-black uppercase tracking-[0.3em] text-[10px]">
              {isJa ? "読み込み中…" : "Loading…"}
            </p>
          </div>
        )}

        {!loading && notFound && (
          <div className="text-center py-32">
            <p className="text-4xl mb-4">📄</p>
            <h1 className="text-2xl font-black text-slate-800 mb-3">
              {isJa ? "ページは利用できません" : "Page Not Available"}
            </h1>
            <p className="text-slate-500 font-medium">
              {isJa
                ? "このページはまだ公開されていません。後でご確認ください。"
                : "This page has not been published yet. Please check back later."}
            </p>
            <Link
              to="/"
              className="mt-8 inline-block px-8 py-3 bg-subidha-blue text-white rounded-2xl font-black text-sm hover:bg-blue-800 transition-colors"
            >
              {isJa ? "ホームへ" : "Go Home"}
            </Link>
          </div>
        )}

        {!loading && page && (
          <>
            <div className="mb-12">
              <p className="text-[10px] font-black uppercase tracking-[0.2em] text-subidha-blue mb-3">
                {category}
              </p>

              <h1 className="text-4xl md:text-5xl font-serif-brand font-black text-slate-900 leading-tight">
                {displayTitle}
              </h1>

              {page.updatedAt && (
                <p className="text-sm text-slate-400 font-medium mt-4">
                  {isJa ? "最終更新：" : "Last updated: "}
                  {new Date(page.updatedAt).toLocaleDateString(
                    isJa ? "ja-JP" : "en-US",
                    { year: "numeric", month: "long", day: "numeric" },
                  )}
                </p>
              )}

              {/* Shown when JA is active but no JA content has been added yet */}
              {showFallbackNotice && (
                <p className="mt-4 inline-flex items-center gap-2 text-xs font-bold text-amber-600 bg-amber-50 border border-amber-200 px-4 py-2 rounded-xl">
                  ⚠️
                  日本語のコンテンツはまだ登録されていません。英語で表示しています。
                </p>
              )}
            </div>

            {/* Rich Text Content — only the active language, never both */}
            <div
              className="prose prose-slate max-w-none prose-headings:font-black prose-a:text-subidha-blue"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(displayContent || "", {
                  ALLOWED_TAGS: [
                    "h1",
                    "h2",
                    "h3",
                    "h4",
                    "h5",
                    "h6",
                    "p",
                    "br",
                    "hr",
                    "strong",
                    "em",
                    "b",
                    "i",
                    "u",
                    "s",
                    "ul",
                    "ol",
                    "li",
                    "blockquote",
                    "pre",
                    "code",
                    "a",
                    "table",
                    "thead",
                    "tbody",
                    "tr",
                    "th",
                    "td",
                  ],
                  ALLOWED_ATTR: ["href", "target", "rel"],
                  FORCE_BODY: true,
                  // Force all links to be safe: block javascript: and data: URIs
                  ALLOW_DATA_ATTR: false,
                  FORBID_ATTR: [
                    "style",
                    "onerror",
                    "onload",
                    "onclick",
                    "onmouseover",
                  ],
                }),
              }}
            />
          </>
        )}
      </main>

      {/* ── Footer ──────────────────────────────────────────────────── */}
      <footer className="border-t border-slate-100 mt-16">
        <div className="max-w-4xl mx-auto px-6 py-8 flex items-center justify-between text-[10px] font-black uppercase tracking-widest text-slate-400">
          <span>© {new Date().getFullYear()} Subidha Inc.</span>
          <Link to="/" className="hover:text-subidha-blue transition-colors">
            {isJa ? "ホーム" : "Home"}
          </Link>
        </div>
      </footer>
    </div>
  );
};

export default ContentPage;
